import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericSegmentedControl from "@ui-components/SegmentedContol/GenericSegmentedContol";
import { TableSkeleton } from "@ui-components/Skeleton/TableSkeleton";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { usePageHeaderActions } from "@shared/navigation/PageHeaderActions";
import type { ParsedFilter } from "kontrolEdge/helpers/advancedFilters";
import CreateKeyModal from "./CreateKeyModal";
import { describeKeyTarget } from "./keyTarget";
import HowToConnect from "./HowToConnect";
import { listApiKeys, revokeApiKey } from "./requests/request";
import {
  SUGGESTED_FILTER_KEYS,
  buildApiKeyColumns,
  fieldValue,
  isExpiringSoon,
  searchableText
} from "./apiKeysData";
import type { ApiKey, ApiKeyListScope } from "./types";

/**
 * API keys — the programmatic credentials that can read this account's data.
 *
 * The page this replaces could not answer the question it existed for: it
 * listed one tenant at a time (whichever the SideNav happened to point at),
 * showed no tenant column, and had no notion of expiry. The scope lens and
 * the Tenant column are the fixes; everything else is deliberately quiet so
 * they stay visible.
 *
 * Layout note, learned the hard way: this page renders no title and no
 * header of its own. The title comes from `getPageTitle` and the controls
 * are registered into the shared title band via `usePageHeaderActions`,
 * the same as every sibling page under Account — a page-local header
 * produced two stacked titles. The usage instructions do not belong here
 * either; they sit behind "How to connect" and inside the create flow,
 * which is the moment you actually need them.
 */

type ScopeOption = "all" | "selected";

const matchesFilters = (key: ApiKey, filters: ParsedFilter[]): boolean =>
  filters.every((filter) => {
    const needle = String(filter.value ?? "").toLowerCase();
    if (!needle) return true;
    // An unkeyed term searches everything the row shows; a keyed one
    // (`tenant:acme`) is scoped to that field.
    const haystack = filter.key
      ? fieldValue(key, filter.key).toLowerCase()
      : searchableText(key);
    return haystack.includes(needle);
  });

const ApiKeysPage: React.FC = () => {
  const api = useAxiosAuth0();
  const { isAuthenticated, isLoading } = useAuth0();
  const selection = useAppSelector((state) => state.selection);
  const { tenantId } = selection;
  const userProfile = useAppSelector((state) => state.userProfile);

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [scopeMode, setScopeMode] = useState<ScopeOption>("all");
  const [resolvedScope, setResolvedScope] = useState<ApiKeyListScope>("own");
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [filters, setFilters] = useState<ParsedFilter[]>([]);
  const [pendingRevoke, setPendingRevoke] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState(false);

  // "All" deliberately omits `?tenantId` — that is what makes the backend
  // answer with the caller's whole visible subtree instead of one tenant.
  const scopedTenantId = scopeMode === "selected" ? tenantId : undefined;

  const fetchKeys = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsFetching(true);
    setError(null);
    try {
      const res = await listApiKeys(api, scopedTenantId);
      setKeys(res.data.data || []);
      setResolvedScope(res.data.scope || "own");
    } catch (err) {
      console.error("[ApiKeysPage] failed to load keys", err);
      setKeys([]);
      setError("We could not load the API keys for this account.");
    } finally {
      setIsFetching(false);
    }
  }, [api, isAuthenticated, scopedTenantId]);

  useEffect(() => {
    if (isLoading) return;
    void fetchKeys();
  }, [fetchKeys, isLoading]);

  const handleConfirmRevoke = async () => {
    if (!pendingRevoke) return;
    setRevoking(true);
    // Optimistic: flip the row to revoked, roll back if the call fails.
    const previous = keys;
    const now = new Date().toISOString();
    setKeys((rows) =>
      rows.map((k) =>
        k.id === pendingRevoke.id
          ? { ...k, revokedAt: now, status: "revoked" as const }
          : k
      )
    );
    try {
      await revokeApiKey(api, pendingRevoke.id, scopedTenantId);
    } catch (err) {
      console.error("[ApiKeysPage] revoke failed", err);
      setKeys(previous);
    } finally {
      setRevoking(false);
      setPendingRevoke(null);
    }
  };

  const stats = useMemo(() => {
    const active = keys.filter((k) => k.status === "active");
    return {
      active: active.length,
      expiringSoon: active.filter(isExpiringSoon).length,
      neverUsed: active.filter((k) => !k.lastUsedAt).length,
      tenants: new Set(keys.map((k) => k.tenant?.name).filter(Boolean)).size
    };
  }, [keys]);

  /**
   * One muted line instead of four cards of big numbers, three of which
   * read zero most of the time. A state is named only when there is one
   * to act on.
   */
  const summary = useMemo(() => {
    if (isFetching && keys.length === 0) return "Loading…";
    if (keys.length === 0) return "No keys issued yet";
    const parts = [`${stats.active} active`];
    if (stats.expiringSoon > 0)
      parts.push(`${stats.expiringSoon} expiring soon`);
    if (stats.neverUsed > 0) parts.push(`${stats.neverUsed} never used`);
    if (stats.tenants > 1) parts.push(`${stats.tenants} tenants`);
    return parts.join(" · ");
  }, [isFetching, keys.length, stats]);

  // Only worth a column when the list actually spans tenants.
  const showTenant = useMemo(
    () =>
      new Set(keys.map((k) => k.tenant?.name ?? "—")).size > 1 ||
      resolvedScope !== "own",
    [keys, resolvedScope]
  );

  const filtered = useMemo(
    () => keys.filter((k) => matchesFilters(k, filters)),
    [keys, filters]
  );

  const columns = useMemo(
    () => buildApiKeyColumns({ showTenant, onRevoke: setPendingRevoke }),
    [showTenant]
  );

  /**
   * The toolbar hides only for an account that has no keys at all and is
   * not filtered — the one case where there is nothing to search and
   * nothing to widen. Any narrowed view keeps it, or the user is stuck.
   */
  const showToolbar = keys.length > 0 || scopeMode === "selected";

  /**
   * The page's controls belong in the shared title band next to "API"
   * (CTX-112), not in a second header of the page's own — every sibling
   * page under Account renders no title of its own for the same reason.
   * Memoized because `usePageHeaderActions` re-registers on node identity.
   */
  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowHowTo(true)}
          className="text-sm text-semantic-text-secondary hover:text-semantic-text-primary transition-colors px-2 py-1"
        >
          How to connect
        </button>
        <GenericButton
          label="Generate key"
          variant="brand"
          size="md"
          onClick={() => setShowCreate(true)}
        />
      </div>
    ),
    []
  );
  usePageHeaderActions(headerActions);

  const renderBody = () => {
    if (error) {
      return (
        <div className="space-y-4 py-6">
          <ErrorFetchingItems text={error} />
          <div className="flex justify-center">
            <GenericButton
              label="Retry"
              variant="secondary"
              size="md"
              onClick={() => {
                void fetchKeys();
              }}
            />
          </div>
        </div>
      );
    }

    if (isFetching && keys.length === 0) {
      return <TableSkeleton columns={6} rows={4} />;
    }

    if (keys.length === 0) {
      // Two different nothings. "This tenant has none" is a filter result
      // and the way out is to widen the lens; "the account has none" is an
      // invitation to create the first one. Showing the invitation for a
      // narrowed view is what made this feel like a dead end.
      const narrowed = scopeMode === "selected";
      return (
        <div className="py-16 text-center">
          <p className="text-sm text-semantic-text-primary">
            {narrowed ? "No keys for this tenant" : "No API keys yet"}
          </p>
          <p className="text-sm text-semantic-text-secondary mt-1">
            {narrowed
              ? "Other tenants in this account may still have keys."
              : "Generate one to give a script or a service read access to your data."}
          </p>
          <div className="mt-5 flex items-center justify-center">
            {narrowed ? (
              <GenericButton
                label="Show all tenants"
                variant="secondary"
                size="md"
                onClick={() => setScopeMode("all")}
              />
            ) : (
              <GenericButton
                label="Generate key"
                variant="brand"
                size="md"
                onClick={() => setShowCreate(true)}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <GenericTable<ApiKey>
        columns={columns}
        data={filtered}
        loading={isFetching}
        initialRowsPerPage={10}
        rowsPerPageOptions={[10, 20, 30]}
        width="100%"
        tableClassName="bg-semantic-bg-primary"
      />
    );
  };

  return (
    <div className="p-6 space-y-5">
      <p className="text-sm text-semantic-text-secondary tabular-nums">
        {summary}
      </p>

      <GenericCard
        isCollapsible={false}
        className="flex flex-col"
        bodyContent={
          <div className="flex flex-col">
            {showToolbar && (
              <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="w-full max-w-full lg:max-w-[420px] min-w-0">
                  {/* Nothing to search when the list is empty, so the input
                      goes; the lens below stays, because it is what emptied
                      the list. */}
                  {keys.length > 0 && (
                    <GenericSearchbar
                      placeholder="Find by name, tenant or status"
                      suggestedKeys={SUGGESTED_FILTER_KEYS}
                      onFiltersChange={setFilters}
                      liveFiltering
                      debounceMs={200}
                    />
                  )}
                </div>
                <div className="shrink-0">
                  {/* The lens the old view lacked: one tenant, or everything
                      this account can reach.

                      It used to be inside the `keys.length > 0` guard, which
                      meant narrowing to a tenant with no keys removed the
                      only control that could widen it again — the view
                      became a dead end you had to reload out of. */}
                  <GenericSegmentedControl
                    options={[
                      { value: "all", label: "All tenants" },
                      { value: "selected", label: "Selected tenant" }
                    ]}
                    value={scopeMode}
                    onChange={(value) => setScopeMode(value as ScopeOption)}
                  />
                </div>
              </div>
            )}

            {renderBody()}
          </div>
        }
      />

      <GenericModal
        isOpen={showHowTo}
        onClose={() => setShowHowTo(false)}
        title="How to connect"
        size="lg"
      >
        <HowToConnect />
      </GenericModal>

      <CreateKeyModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={fetchKeys}
        tenantId={scopedTenantId ?? tenantId}
        target={describeKeyTarget(selection, {
          tenantId: userProfile.tenantId,
          tenantName: userProfile.Tenant?.nameDisplay || userProfile.Tenant?.name,
          tenantType: userProfile.TenantType
        })}
      />

      <GenericAlert
        isOpen={!!pendingRevoke}
        onClose={() => !revoking && setPendingRevoke(null)}
        onConfirm={handleConfirmRevoke}
        title="Revoke API key?"
        description={
          pendingRevoke ? (
            <span>
              <span className="font-medium">{pendingRevoke.name}</span>
              {pendingRevoke.tenant?.name
                ? ` (${pendingRevoke.tenant.name})`
                : ""}{" "}
              will stop working immediately. This cannot be undone.
            </span>
          ) : null
        }
        confirmLabel={revoking ? "Revoking…" : "Revoke"}
        cancelLabel="Cancel"
        variant="warning"
        confirmButtonDisabled={revoking}
      />
    </div>
  );
};

export default ApiKeysPage;
